import type {
  AdminCrewReportStatisticsGridProps,
  AdminCrewReportStatisticsView,
} from "../../models/admin-crew-report.models";
import { printAdminCrewReportStatisticsPdfExport } from "../../utils/admin-crew-report-statistics.utils";
import {
  ADMIN_CREW_REPORT_SECTION_TITLE_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_GRID_ID,
  ADMIN_CREW_REPORT_STATISTICS_PRINT_PREPARATION_DELAY_MS,
  ADMIN_CREW_REPORT_STATISTICS_STATE_CARD_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_TOGGLE_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS,
} from "../../utils/admin-crew-report.constants";
import AdminCrewReportStatisticsCards from "./AdminCrewReportStatisticsCards";
import AdminCrewReportStatisticsPdfExportButton from "./AdminCrewReportStatisticsPdfExportButton";
import AdminCrewReportStatisticsPdfExportView from "./AdminCrewReportStatisticsPdfExportView";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportStatisticsGrid = ({ status, statistics, errorMessage }: AdminCrewReportStatisticsGridProps) => {
  const [requestedView, setRequestedView] = useState<"crew" | "women">("crew");
  const [pdfExportView, setPdfExportView] = useState<AdminCrewReportStatisticsView | null>(null);
  const hasWomenOnBoardStatistics = Boolean(statistics?.womenOnBoard);
  const view = requestedView === "women" && hasWomenOnBoardStatistics ? "women" : "crew";
  const viewLabels = ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS[view];
  const displayedStatistics = view === "women" ? statistics?.womenOnBoard : statistics;
  const hasRenderableStatistics = status !== "error" && Boolean(statistics && displayedStatistics);
  const isPdfExportDisabled = status !== "loaded" || !hasRenderableStatistics || pdfExportView !== null;

  useEffect(() => {
    if (!pdfExportView) {
      return;
    }

    let cleanupPrintMode: (() => void) | undefined;
    const preparationTimeoutId = window.setTimeout(() => {
      cleanupPrintMode = printAdminCrewReportStatisticsPdfExport(() => setPdfExportView(null));
    }, ADMIN_CREW_REPORT_STATISTICS_PRINT_PREPARATION_DELAY_MS);

    return () => {
      window.clearTimeout(preparationTimeoutId);
      cleanupPrintMode?.();
    };
  }, [pdfExportView]);

  return (
    <section
      aria-label={viewLabels.sectionAriaLabel}
      data-test="admin-crew-report-statistics-section"
      data-testid="admin-crew-report-statistics-section"
      className="bg-white p-4 shadow-sm dark:bg-gray-800"
    >
      <header
        data-test="admin-crew-report-statistics-header"
        data-testid="admin-crew-report-statistics-header"
        className="mb-4 flex items-center justify-between gap-3"
      >
        <h2
          aria-label={viewLabels.sectionTitle}
          className={twMerge(ADMIN_CREW_REPORT_SECTION_TITLE_CLASS_NAME, "mb-0 whitespace-nowrap")}
        >
          <AdminCrewReportStatisticsPdfExportButton
            disabled={isPdfExportDisabled}
            view={view}
            onClick={() => setPdfExportView(view)}
          />
        </h2>
        <button
          type="button"
          aria-controls={ADMIN_CREW_REPORT_STATISTICS_GRID_ID}
          data-test="admin-crew-report-statistics-toggle"
          className={ADMIN_CREW_REPORT_STATISTICS_TOGGLE_CLASS_NAME}
          disabled={!hasWomenOnBoardStatistics}
          onClick={() => setRequestedView(view === "crew" ? "women" : "crew")}
        >
          {viewLabels.toggleButtonLabel}
        </button>
      </header>
      <div
        id={ADMIN_CREW_REPORT_STATISTICS_GRID_ID}
        data-test="admin-crew-report-statistics-grid"
        data-testid="admin-crew-report-statistics-grid"
        className={twMerge(
          "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3",
          hasRenderableStatistics && "xl:grid-rows-[250px_250px_250px]",
        )}
      >
        {status === "loading" && !statistics ? (
          <article
            data-testid="admin-crew-report-statistics-state-card"
            className={ADMIN_CREW_REPORT_STATISTICS_STATE_CARD_CLASS_NAME}
            aria-busy="true"
            aria-live="polite"
          >
            <h2 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>Months On Board</h2>
            <div className="flex flex-1 items-center justify-center text-sm text-[#65706F] dark:text-gray-300">
              Loading statistics…
            </div>
          </article>
        ) : status === "error" || !statistics || !displayedStatistics ? (
          <article
            data-testid="admin-crew-report-statistics-state-card"
            className={ADMIN_CREW_REPORT_STATISTICS_STATE_CARD_CLASS_NAME}
          >
            <h2 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>Months On Board</h2>
            <div
              className="flex flex-1 items-center justify-center px-4 text-center text-sm text-[#BF0002]"
              role="alert"
            >
              {errorMessage || "Crew statistics could not be loaded."}
            </div>
          </article>
        ) : (
          <AdminCrewReportStatisticsCards statistics={statistics} view={view} />
        )}
      </div>
      {statistics && errorMessage && (
        <p className="mt-2 text-xs font-medium text-[#BF0002]" role="alert">
          {errorMessage}
        </p>
      )}
      {pdfExportView && statistics && (
        <AdminCrewReportStatisticsPdfExportView statistics={statistics} view={pdfExportView} />
      )}
    </section>
  );
};

export default AdminCrewReportStatisticsGrid;
